/**
 * Rating Button Komponente
 * Custom Element für den Bewertungs-Button, der mit einem Rating-Widget verknüpft ist
 */

export class RatingButton extends HTMLElement {
    constructor() {
        super()
        // Button-Eigenschaften
        this.targetWidget = null
        this.connected = false
    }

    /**
     * Wird aufgerufen, wenn das Element dem DOM hinzugefügt wird
     */
    connectedCallback() {
        // Button einrichten
        this.setupButton()

        // Nur einmal Event-Listener hinzufügen
        if (!this.connected) {
            this.addEventListener('click', this.handleClick.bind(this))
            this.connected = true
            
            // Event-Listener für allgemeine Rating-Status-Änderungen
            document.addEventListener('rating-state-changed', this.handleRatingStateChange.bind(this))
        }
        
        // Überprüfen, ob das Ziel-Widget bereits bewertet wurde
        this.checkIfAlreadyRated()
    }

    /**
     * Richtet den Button ein und verbindet ihn mit dem Ziel-Widget
     */
    setupButton() {
        // Ziel-Widget-ID aus Attribut abrufen
        const targetId = this.getAttribute('target')
        if (!targetId) {
            return
        }

        // Ziel-Widget finden
        this.targetWidget = document.getElementById(targetId)
        if (!this.targetWidget) {
            return
        }

        
        // Überprüfen, ob das Ziel-Widget bereits bewertet wurde
        this.checkIfAlreadyRated()
    }

    /**
     * Behandelt Klick-Events auf den Button
     */
    handleClick() {
        // Wenn der Button deaktiviert ist und nicht im Debug-Modus, nichts tun
        if ((this.hasAttribute('disabled') || this.getAttribute('aria-disabled') === 'true') && 
            !this.isInDebugMode()) {
            return
        }
        
        if (!this.targetWidget) {
            // Versuch, das Widget erneut zu finden
            const targetId = this.getAttribute('target')
            if (targetId) {
                this.targetWidget = document.getElementById(targetId)
            }

            if (!this.targetWidget) {
                return
            }
        }

        // Modal des Ziel-Widgets öffnen
        if (typeof this.targetWidget.openRatingModal === 'function') {
            this.targetWidget.openRatingModal()
        } else {
        }
    }
    
    /**
     * Prüft, ob das verknüpfte Widget im Debug-Modus ist
     * @returns {boolean} true wenn im Debug-Modus
     */
    isInDebugMode() {
        if (!this.targetWidget || !this.targetWidget.store || 
            typeof this.targetWidget.store.getState !== 'function') {
            return false;
        }
        
        const state = this.targetWidget.store.getState();
        return state && state.debugMode === true;
    }

    /**
     * Attributänderungen beobachten
     */
    static get observedAttributes() {
        return ['target']
    }

    /**
     * Wird bei Attributänderungen aufgerufen
     * @param {string} name - Name des geänderten Attributs
     * @param {string} oldValue - Alter Wert
     * @param {string} newValue - Neuer Wert
     */
    attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'target' && oldValue !== newValue) {
            this.setupButton()
        }
    }
    
    /**
     * Behandelt Rating-Status-Änderungs-Events
     * @param {CustomEvent} event - Das Rating-State-Event
     */
    handleRatingStateChange(event) {
        if (!this.targetWidget || !event.detail || !event.detail.pid) return;
        
        // Prüfen, ob die Änderung für unsere verknüpfte PID gilt
        if (this.targetWidget.ratingPid === event.detail.pid && event.detail.hasRated) {
            this.disableButton();
        }
    }
    
    /**
     * Überprüft, ob das verknüpfte Widget bereits bewertet wurde
     */
    checkIfAlreadyRated() {
        if (!this.targetWidget) return;
        
        let hasRated = false;
        
        // 1. Prüfen, ob im globalen State für diese PID bereits ein Rating vorliegt
        if (window.RatingGlobalState && window.RatingGlobalState.ratedPages && this.targetWidget.ratingPid) {
            hasRated = window.RatingGlobalState.ratedPages.get(this.targetWidget.ratingPid) || false;
        }
        
        // 2. Falls nicht, prüfen ob das Widget einen Status-Container mit "already-rated" Klasse hat
        if (!hasRated) {
            const statusContainer = this.targetWidget.querySelector('.status-container.already-rated');
            hasRated = !!statusContainer;
        }
        
        // 3. Als letztes noch den Store des Widgets prüfen
        if (!hasRated && this.targetWidget.store && typeof this.targetWidget.store.getState === 'function') {
            const state = this.targetWidget.store.getState();
            hasRated = state.hasRated;
        }
        
        // Button deaktivieren, wenn der Artikel bereits bewertet wurde
        if (hasRated) {
            // Globalen State aktualisieren, falls noch nicht geschehen
            if (window.dispatchRatingStateEvent && this.targetWidget.ratingPid) {
                window.dispatchRatingStateEvent(this.targetWidget.ratingPid, true);
            }
            
            this.disableButton();
        }
    }
    
    /**
     * Deaktiviert den Button und ggf. die Offerbubble
     */
    disableButton() {
        // Button deaktivieren
        this.setAttribute('disabled', '');
        this.setAttribute('aria-disabled', 'true');
        this.classList.add('btn--disabled');
        
        // Offerbubble deaktivieren, falls vorhanden
        const offerBubble = document.getElementById('offerbubble');
        if (offerBubble) {
            offerBubble.setAttribute('aria-disabled', 'true');
        }
    }
}

// Registrieren des Custom Elements
customElements.define('rating-button', RatingButton)
